// 极简请求缓存：同 key 的并发请求会共享一个 in-flight promise，
// 成功结果按 staleTime 缓存，失效后下次调用才会重新发起请求。
// 能力对齐 @tanstack/vue-query 中本项目真正用到的两个方法：fetchQuery / removeQueries。

const DEFAULT_STALE_TIME = 5 * 60 * 1000

const cache = new Map()     // serializedKey -> { data, storedAt, staleTime }
const inflight = new Map()  // serializedKey -> Promise

function serialize(key) {
  return JSON.stringify(Array.isArray(key) ? key : [key])
}

function isServer() {
  return typeof process !== 'undefined' && !!process.server
}

function matchesPrefix(serializedKey, prefix) {
  try {
    const parsed = JSON.parse(serializedKey)
    if (!Array.isArray(parsed) || parsed.length < prefix.length) return false
    for (let i = 0; i < prefix.length; i++) {
      if (parsed[i] !== prefix[i]) return false
    }
    return true
  } catch {
    return false
  }
}

export function fetchQuery({ queryKey, queryFn, staleTime = DEFAULT_STALE_TIME }) {
  // SSR 阶段不做跨请求缓存，避免不同用户之间的数据串扰
  if (isServer()) return Promise.resolve().then(queryFn)

  const k = serialize(queryKey)

  const hit = cache.get(k)
  if (hit && Date.now() - hit.storedAt < hit.staleTime) {
    return Promise.resolve(hit.data)
  }

  const pending = inflight.get(k)
  if (pending) return pending

  const p = Promise.resolve().then(queryFn).then(
    (data) => {
      cache.set(k, { data, storedAt: Date.now(), staleTime })
      inflight.delete(k)
      return data
    },
    (err) => {
      inflight.delete(k)
      throw err
    }
  )
  inflight.set(k, p)
  return p
}

// 按「key 前缀」失效缓存：removeQueries(['projectVipInfo']) 会同时命中
// ['projectVipInfo'] 与 ['projectVipInfo', projectId] 之类的分项 key。
export function removeQueries(queryKey) {
  const prefix = Array.isArray(queryKey) ? queryKey : [queryKey]
  for (const k of [...cache.keys()]) {
    if (matchesPrefix(k, prefix)) cache.delete(k)
  }
  for (const k of [...inflight.keys()]) {
    if (matchesPrefix(k, prefix)) inflight.delete(k)
  }
}

export const queryCache = { fetchQuery, removeQueries }
